import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Layers, Loader2, ShieldCheck, Users, Ban, MonitorSmartphone } from 'lucide-react';
import { useDashboardV3, setStoredTestView } from '../../contexts/DashboardV3Context';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENCON_ORANGE = '#ee7203';
const PURPLE = '#7c3aed';

const MODE_OPTIONS = [
  { value: 'off', label: 'Off', hint: 'Nobody sees V3', icon: Ban },
  { value: 'super_admin', label: 'Super admins only', hint: 'Only super admins see V3 here', icon: ShieldCheck },
  { value: 'global', label: 'Everyone', hint: 'Everyone on this UI sees V3', icon: Users },
];

const UiCard = ({ title, subtitle, tag, value, busy, onChange, testId }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <h4 className="text-base font-bold text-gray-900">{title}</h4>
        <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
      </div>
      <span className="text-[11px] font-extrabold text-white px-2.5 py-1 rounded-full flex-shrink-0" style={{ background: PURPLE }}>{tag}</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {MODE_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={busy}
            onClick={() => onChange(opt.value)}
            className="text-left rounded-lg border-2 px-3 py-2.5 transition-colors disabled:opacity-60"
            style={active ? { borderColor: ENCON_ORANGE, background: '#fff7ed' } : { borderColor: '#e5e7eb', background: '#fff' }}
            data-testid={`${testId}-${opt.value}`}
          >
            <div className="flex items-center gap-1.5 text-sm font-bold" style={{ color: active ? ENCON_ORANGE : '#374151' }}>
              <Icon className="w-3.5 h-3.5" /> {opt.label}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">{opt.hint}</div>
          </button>
        );
      })}
    </div>
  </div>
);

/** Settings > Dashboard V3 (super admin only): which people see the V3 view of the Main UI and of the Limited UI, and a test switch
 *  so a super admin can look at the dashboard as the Main UI or the Limited UI. */
export const DashboardV3Settings = () => {
  const { settings, testView, refresh } = useDashboardV3();
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(settings);

  useEffect(() => { setLocal(settings); }, [settings]);

  const save = async (key, value, label) => {
    if (value === 'global' && !window.confirm(`Show the V3 view of the ${label} to EVERYONE on it?`)) return;
    setBusy(true);
    try {
      const res = await api.put(`${API}/dashboard-v3/settings`, { [key]: value });
      setLocal({ main_ui: res.data.main_ui, limited_ui: res.data.limited_ui });
      toast.success(`${label} V3: ${MODE_OPTIONS.find((o) => o.value === value)?.label}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="dashboard-v3-settings">
      <div className="flex items-center gap-2">
        <Layers className="w-5 h-5" style={{ color: PURPLE }} />
        <h3 className="text-lg font-bold text-gray-900">Dashboard V3 — UI management</h3>
      </div>
      <p className="text-xs text-gray-500 leading-relaxed max-w-3xl">
        Controls who sees the new V3 look of the dashboards. While V3 is on for you, a small tag shows in the bottom-left corner of the
        screen — <strong>Main UI V3</strong> or <strong>Ltd UI V3</strong> — so you can always tell which view you are looking at. The
        Main UI and the Limited UI are switched on separately.
      </p>

      <UiCard
        title="Main UI" subtitle="The full dashboard — everyone who is not on Limited Access."
        tag="Main UI V3" value={local.main_ui} busy={busy} testId="v3-main-ui"
        onChange={(v) => save('main_ui', v, 'Main UI')}
      />
      <UiCard
        title="Limited UI" subtitle="The cut-down dashboard for users on Limited Access."
        tag="Ltd UI V3" value={local.limited_ui} busy={busy} testId="v3-limited-ui"
        onChange={(v) => save('limited_ui', v, 'Limited UI')}
      />

      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3" data-testid="v3-test-view">
        <div className="flex items-start gap-2">
          <MonitorSmartphone className="w-4 h-4 mt-0.5 text-gray-500" />
          <div>
            <h4 className="text-base font-bold text-gray-900">Test view (super admins)</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              A super admin normally only ever sees the Main UI. Switch this to look at the Limited UI exactly as a limited user would
              (for testing). It only changes what <em>you</em> see, in this browser. A button in the bottom-left corner always takes you
              back to the Main UI.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {[{ v: 'main', label: 'Main UI' }, { v: 'limited', label: 'Limited UI' }].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => { setStoredTestView(o.v); toast.success(`Now viewing the ${o.label}`); }}
              className="px-4 py-2 rounded-lg border-2 text-sm font-bold transition-colors"
              style={testView === o.v ? { borderColor: PURPLE, background: PURPLE, color: '#fff' } : { borderColor: '#e5e7eb', background: '#fff', color: '#374151' }}
              data-testid={`v3-test-view-${o.v}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      {busy && <div className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</div>}
    </div>
  );
};

export default DashboardV3Settings;
